import type { VersionedType } from 'api-client';
import invariant from 'invariant';
import { groupByKey } from 'utils/reduce-utils';
import shallow from 'stores/compare/shallow';

// TODO Add support to composite keys (objects without Id attribute)
function compareVersions(objA, objB) {
  if (!objA || !objB) {
    return Object.is(objA, objB);
  }
  return objA.id === objB.id && objA.version === objB.version;
}

function sameFn(compareFn: (a: any, b: any) => boolean) {
  return function innerEqual(a: any, b: any): boolean {
    invariant(!a || !b || typeof a === typeof b, 'Compared items must be of same type');

    const compareFalsey = !a || !b;

    // TODO not the best way to test if an object is a simple object or a map
    const compareSingle = (a && 'version' in a) || (b && 'version' in b);
    if (compareFalsey || compareSingle) {
      return compareFn(a, b);
    }

    if (typeof a[Symbol.iterator] === 'function' || typeof b[Symbol.iterator] === 'function') {
      const aArray = [...a];
      const bArray = [...b];
      if ((aArray[0] && 'id' in aArray[0]) || (bArray[0] && 'id' in bArray[0])) {
        const aIds = aArray.map((aObj) => aObj?.id);
        const bIds = bArray.map((bObj) => bObj?.id);
        const sameSequence = shallow(aIds, bIds);
        return sameSequence && innerEqual(groupByKey(aArray, 'id'), groupByKey(bArray, 'id'));
      }
      return shallow(aArray, bArray);
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    return aKeys.length === bKeys.length && aKeys.every((key) => compareFn(a[key], b[key]));
  };
}

const sameVersion = sameFn(compareVersions);

const isVersionType = (payload: any): payload is VersionedType =>
  payload && typeof payload === 'object' && 'version' in payload;

function previewVersion<T extends string | VersionedType>(payload: T, increment: number = 1): T {
  const isObject = isVersionType(payload);
  const [version, localVersion = version] = String(
    (isObject ? payload.version : payload) || 0
  ).split(':');
  const next = `${version}:${+localVersion + increment}`;
  return isVersionType(payload) ? ({ version: next } as T) : (next as T);
}

function versionNumber<T extends number | string | VersionedType>(payload: T): number {
  const [version, preview = version] = String(
    typeof payload === 'object' && 'version' in payload ? payload.version : payload
  ).split(':');
  return +preview;
}

function nextVersion<T extends number | string | VersionedType>(
  payload: T,
  increment: number = 1
): T {
  const isObject = payload && typeof payload === 'object' && 'version' in payload;
  const version = versionNumber(isObject ? (payload as any).version : payload) || 0;
  const next = version + increment;
  return isObject ? { ...(payload as any), version: next } : (next as T);
}

export default sameVersion;
export { previewVersion, versionNumber, sameFn, nextVersion };
